import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from 'sonner';
import {
  Leaf,
  Award,
  Shield,
  FileCheck,
  Calendar,
  AlertCircle,
  CheckCircle2,
  Plus,
  FileText,
  Upload,
  Loader2,
  ClipboardCheck,
  Droplets,
  Tractor,
  Search,
  ExternalLink,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Icônes et couleurs par certification
const CERT_CONFIG = {
  bio: { icon: Leaf, color: 'text-green-600', bg: 'bg-green-100', label: 'Bio AB' },
  hve: { icon: Shield, color: 'text-blue-600', bg: 'bg-blue-100', label: 'HVE' },
  fair_for_life: { icon: Award, color: 'text-orange-600', bg: 'bg-orange-100', label: 'Fair for Life' },
  demeter: { icon: Leaf, color: 'text-purple-600', bg: 'bg-purple-100', label: 'Demeter' },
  nature_progres: { icon: Leaf, color: 'text-lime-600', bg: 'bg-lime-100', label: 'Nature & Progrès' },
  global_gap: { icon: Shield, color: 'text-cyan-600', bg: 'bg-cyan-100', label: 'GlobalG.A.P.' },
  bas_carbone: { icon: Leaf, color: 'text-indigo-600', bg: 'bg-indigo-100', label: 'Label Carbone' },
};

export default function CertificationsPage() {
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState('certifications');
  
  // États certifications
  const [certifications, setCertifications] = useState([]);
  const [certTypes, setCertTypes] = useState({});
  const [loading, setLoading] = useState(true);
  const [showNewCertDialog, setShowNewCertDialog] = useState(false);
  
  // États formulaire nouvelle certification
  const [newCert, setNewCert] = useState({
    type_certification: '',
    organisme: '',
    numero_certificat: '',
    date_obtention: '',
    date_expiration: '',
    surface_certifiee_ha: '',
    notes: ''
  });
  
  // États certificats de lavage (ETA)
  const [certificatsLavage, setCertificatsLavage] = useState([]);
  const [showLavageDialog, setShowLavageDialog] = useState(false);
  const [newLavage, setNewLavage] = useState({
    materiel_nom: '',
    type_lavage: 'complet',
    date_lavage: new Date().toISOString().split('T')[0],
    heure_lavage: new Date().toTimeString().slice(0, 5),
    operateur_nom: '',
    client_nom: '',
    notes: ''
  });
  
  // États produits bio
  const [produitsBio, setProduitsBio] = useState({});
  const [searchProduit, setSearchProduit] = useState('');
  const [verificationResult, setVerificationResult] = useState(null);

  // Charger les données
  useEffect(() => {
    loadData();
  }, [token]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [certTypesRes, certsRes, lavageRes, produitsRes] = await Promise.all([
        fetch(`${API_URL}/api/certifications/types`, {
          headers: { Authorization: `Bearer ${token}`, ...(localStorage.getItem('activeSocieteId') ? { 'X-Societe-Id': localStorage.getItem('activeSocieteId') } : {}) }
        }),
        fetch(`${API_URL}/api/certifications`, {
          headers: { Authorization: `Bearer ${token}`, ...(localStorage.getItem('activeSocieteId') ? { 'X-Societe-Id': localStorage.getItem('activeSocieteId') } : {}) }
        }),
        fetch(`${API_URL}/api/certifications/lavage`, {
          headers: { Authorization: `Bearer ${token}`, ...(localStorage.getItem('activeSocieteId') ? { 'X-Societe-Id': localStorage.getItem('activeSocieteId') } : {}) }
        }),
        fetch(`${API_URL}/api/certifications/produits-bio`, {
          headers: { Authorization: `Bearer ${token}`, ...(localStorage.getItem('activeSocieteId') ? { 'X-Societe-Id': localStorage.getItem('activeSocieteId') } : {}) }
        })
      ]);

      if (certTypesRes.ok) {
        const data = await certTypesRes.json();
        setCertTypes(data.certifications || {});
      }
      if (certsRes.ok) {
        const data = await certsRes.json();
        setCertifications(data.certifications || []);
      }
      if (lavageRes.ok) {
        const data = await lavageRes.json();
        setCertificatsLavage(data.certificats || []);
      }
      if (produitsRes.ok) {
        const data = await produitsRes.json();
        setProduitsBio(data.produits || {});
      }
    } catch (error) {
      console.error('Erreur chargement:', error);
    } finally {
      setLoading(false);
    }
  };

  // Créer une certification
  const createCertification = async () => {
    try {
      const res = await fetch(`${API_URL}/api/certifications`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newCert)
      });

      if (res.ok) {
        toast.success('Certification ajoutée');
        setShowNewCertDialog(false);
        setNewCert({
          type_certification: '',
          organisme: '',
          numero_certificat: '',
          date_obtention: '',
          date_expiration: '',
          surface_certifiee_ha: '',
          notes: ''
        });
        loadData();
      } else {
        toast.error('Erreur lors de la création');
      }
    } catch (error) {
      toast.error('Erreur réseau');
    }
  };

  // Créer un certificat de lavage
  const createLavage = async () => {
    try {
      const res = await fetch(`${API_URL}/api/certifications/lavage`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...newLavage,
          materiel_id: 'manual-' + Date.now()
        })
      });

      if (res.ok) {
        toast.success('Certificat de lavage enregistré');
        setShowLavageDialog(false);
        setNewLavage({
          materiel_nom: '',
          type_lavage: 'complet',
          date_lavage: new Date().toISOString().split('T')[0],
          heure_lavage: new Date().toTimeString().slice(0, 5),
          operateur_nom: '',
          client_nom: '',
          notes: ''
        });
        loadData();
      } else {
        toast.error('Erreur lors de la création');
      }
    } catch (error) {
      toast.error('Erreur réseau');
    }
  };

  // Vérifier si un produit est bio
  const verifierProduit = async () => {
    if (!searchProduit.trim()) return;
    
    try {
      const res = await fetch(`${API_URL}/api/certifications/verifier-produit/${encodeURIComponent(searchProduit)}`, {
        headers: { Authorization: `Bearer ${token}`, ...(localStorage.getItem('activeSocieteId') ? { 'X-Societe-Id': localStorage.getItem('activeSocieteId') } : {}) }
      });
      
      if (res.ok) {
        const data = await res.json();
        setVerificationResult(data);
      }
    } catch (error) {
      console.error('Erreur vérification:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="certifications-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-full bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center">
            <Award className="w-7 h-7 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">Certifications & Audits</h1>
            <p className="text-muted-foreground">
              Bio, HVE, Fair for Life, Carbone - Gestion complète de vos certifications
            </p>
          </div>
        </div>
        <Button onClick={() => setShowNewCertDialog(true)}>
          <Plus className="w-4 h-4 mr-2" />
          Nouvelle certification
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="certifications" className="flex items-center gap-2">
            <Award className="w-4 h-4" />
            Mes certifications
          </TabsTrigger>
          <TabsTrigger value="lavage" className="flex items-center gap-2">
            <Droplets className="w-4 h-4" />
            Certificats lavage
          </TabsTrigger>
          <TabsTrigger value="produits" className="flex items-center gap-2">
            <Search className="w-4 h-4" />
            Produits Bio (EFI)
          </TabsTrigger>
        </TabsList>

        {/* Tab Certifications */}
        <TabsContent value="certifications" className="space-y-6">
          {/* Types de certifications disponibles */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Types de certifications supportées</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
                {Object.entries(certTypes).map(([key, cert]) => {
                  const config = CERT_CONFIG[key] || { icon: Award, color: 'text-gray-600', bg: 'bg-gray-100' };
                  const Icon = config.icon;
                  const activeCert = certifications.find(c => c.type_certification === key);
                  
                  return (
                    <div 
                      key={key}
                      className={`p-4 rounded-lg text-center ${config.bg} ${activeCert ? 'ring-2 ring-green-500' : ''}`}
                    >
                      <Icon className={`w-8 h-8 mx-auto mb-2 ${config.color}`} />
                      <p className="font-medium text-sm">{cert.nom_court || config.label}</p>
                      {activeCert ? (
                        <Badge className="mt-2 text-xs bg-green-500">Certifié</Badge>
                      ) : (
                        <Badge variant="outline" className="mt-2 text-xs">Non certifié</Badge>
                      )}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Liste des certifications actives */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileCheck className="w-5 h-5" />
                Mes certifications actives ({certifications.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {certifications.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Award className="w-12 h-12 mx-auto mb-3 opacity-20" />
                  <p>Aucune certification enregistrée</p>
                  <Button variant="outline" className="mt-4" onClick={() => setShowNewCertDialog(true)}>
                    Ajouter ma première certification
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {certifications.map((cert, i) => {
                    const config = CERT_CONFIG[cert.type_certification] || { icon: Award, color: 'text-gray-600', bg: 'bg-gray-100' };
                    const Icon = config.icon;
                    
                    return (
                      <div key={i} className="flex items-center justify-between p-4 border rounded-lg">
                        <div className="flex items-center gap-4">
                          <div className={`w-12 h-12 rounded-lg ${config.bg} flex items-center justify-center`}>
                            <Icon className={`w-6 h-6 ${config.color}`} />
                          </div>
                          <div>
                            <h4 className="font-semibold">{cert.type_nom || config.label}</h4>
                            <p className="text-sm text-muted-foreground">
                              {cert.organisme} {cert.numero_certificat && `- N° ${cert.numero_certificat}`}
                            </p>
                            <div className="flex items-center gap-2 mt-1">
                              <Calendar className="w-3 h-3 text-muted-foreground" />
                              <span className="text-xs text-muted-foreground">
                                Expire le {new Date(cert.date_expiration).toLocaleDateString('fr-FR')}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {cert.jours_restants !== undefined && (
                            <Badge variant={
                              cert.statut_expiration === 'ok' ? 'default' :
                              cert.statut_expiration === 'attention' ? 'secondary' : 'destructive'
                            }>
                              {cert.jours_restants > 0 ? `${cert.jours_restants}j restants` : 'Expiré'}
                            </Badge>
                          )}
                          <Button variant="outline" size="sm">
                            <FileText className="w-4 h-4 mr-1" />
                            Dossier audit
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab Certificats de lavage */}
        <TabsContent value="lavage" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Droplets className="w-5 h-5" />
                    Certificats de lavage du matériel
                  </CardTitle>
                  <CardDescription>
                    Obligatoire pour les ETA intervenant sur des parcelles bio
                  </CardDescription>
                </div>
                <Button onClick={() => setShowLavageDialog(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Nouveau certificat
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {certificatsLavage.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Tractor className="w-12 h-12 mx-auto mb-3 opacity-20" />
                  <p>Aucun certificat de lavage enregistré</p>
                  <p className="text-sm mt-1">Les certificats de lavage sont requis pour les audits bio</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {certificatsLavage.map((cert, i) => (
                    <div key={i} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                          <Tractor className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <p className="font-medium">{cert.materiel_nom}</p>
                          <p className="text-sm text-muted-foreground">
                            {cert.date_lavage} à {cert.heure_lavage}
                            {cert.operateur_nom && ` - ${cert.operateur_nom}`}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline">{cert.type_lavage}</Badge>
                        {cert.client_nom && (
                          <Badge variant="secondary">Client: {cert.client_nom}</Badge>
                        )}
                        <CheckCircle2 className="w-5 h-5 text-green-500" />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab Produits Bio */}
        <TabsContent value="produits" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Search className="w-5 h-5" />
                Vérifier un produit
              </CardTitle>
              <CardDescription>
                Vérifiez si un produit phytosanitaire ou engrais est autorisé en agriculture biologique
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-2">
                <Input
                  placeholder="Nom du produit (ex: Soufre, Bouillie bordelaise...)"
                  value={searchProduit}
                  onChange={(e) => setSearchProduit(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && verifierProduit()}
                />
                <Button onClick={verifierProduit}>
                  <Search className="w-4 h-4 mr-2" />
                  Vérifier
                </Button>
              </div>

              {verificationResult && (
                <div className={`p-4 rounded-lg ${
                  verificationResult.autorise_bio ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
                } border`}>
                  <div className="flex items-start gap-3">
                    {verificationResult.autorise_bio ? (
                      <CheckCircle2 className="w-6 h-6 text-green-600 flex-shrink-0" />
                    ) : (
                      <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0" />
                    )}
                    <div>
                      <p className="font-semibold">{verificationResult.message}</p>
                      {verificationResult.produit && (
                        <div className="mt-2 text-sm text-muted-foreground">
                          {verificationResult.produit.dose_max && (
                            <p>Dose max: {verificationResult.produit.dose_max}</p>
                          )}
                          {verificationResult.produit.cultures && (
                            <p>Cultures: {verificationResult.produit.cultures.join(', ')}</p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              <div className="pt-4 border-t">
                <p className="text-sm text-muted-foreground mb-3">
                  Source: Base EFI/E-Phy simplifiée. Pour une vérification officielle:
                </p>
                <Button variant="outline" asChild>
                  <a href="https://ephy.anses.fr" target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Consulter E-Phy (ANSES)
                  </a>
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Liste des produits autorisés */}
          <Card>
            <CardHeader>
              <CardTitle>Produits autorisés en Bio</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-4">
                {Object.entries(produitsBio).map(([categorie, produits]) => (
                  <div key={categorie} className="border rounded-lg p-4">
                    <h4 className="font-semibold capitalize mb-3">{categorie}</h4>
                    <div className="space-y-2">
                      {produits.map((produit, i) => (
                        <div key={i} className="flex items-center justify-between text-sm">
                          <span>{produit.nom}</span>
                          {produit.amm && (
                            <Badge variant="outline" className="text-xs">AMM</Badge>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Dialog Nouvelle Certification */}
      <Dialog open={showNewCertDialog} onOpenChange={setShowNewCertDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Ajouter une certification</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Type de certification</Label>
              <Select 
                value={newCert.type_certification} 
                onValueChange={(val) => setNewCert(prev => ({...prev, type_certification: val}))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner..." />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(certTypes).map(([key, cert]) => (
                    <SelectItem key={key} value={key}>{cert.nom}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Organisme certificateur</Label>
              <Select 
                value={newCert.organisme} 
                onValueChange={(val) => setNewCert(prev => ({...prev, organisme: val}))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner..." />
                </SelectTrigger>
                <SelectContent>
                  {newCert.type_certification && certTypes[newCert.type_certification]?.organismes?.map((org, i) => (
                    <SelectItem key={i} value={org}>{org}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Numéro de certificat</Label>
              <Input 
                value={newCert.numero_certificat}
                onChange={(e) => setNewCert(prev => ({...prev, numero_certificat: e.target.value}))}
                placeholder="Optionnel"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Date d'obtention</Label>
                <Input 
                  type="date"
                  value={newCert.date_obtention}
                  onChange={(e) => setNewCert(prev => ({...prev, date_obtention: e.target.value}))}
                />
              </div>
              <div className="space-y-2">
                <Label>Date d'expiration</Label>
                <Input 
                  type="date"
                  value={newCert.date_expiration}
                  onChange={(e) => setNewCert(prev => ({...prev, date_expiration: e.target.value}))}
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Surface certifiée (ha)</Label>
              <Input 
                type="number"
                value={newCert.surface_certifiee_ha}
                onChange={(e) => setNewCert(prev => ({...prev, surface_certifiee_ha: e.target.value}))}
                placeholder="Optionnel"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewCertDialog(false)}>
              Annuler
            </Button>
            <Button onClick={createCertification} disabled={!newCert.type_certification || !newCert.organisme}>
              Ajouter
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Certificat de Lavage */}
      <Dialog open={showLavageDialog} onOpenChange={setShowLavageDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nouveau certificat de lavage</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Matériel</Label>
              <Input 
                value={newLavage.materiel_nom}
                onChange={(e) => setNewLavage(prev => ({...prev, materiel_nom: e.target.value}))}
                placeholder="Ex: Pulvérisateur Berthoud"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Type de lavage</Label>
              <Select 
                value={newLavage.type_lavage} 
                onValueChange={(val) => setNewLavage(prev => ({...prev, type_lavage: val}))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="complet">Complet</SelectItem>
                  <SelectItem value="partiel">Partiel</SelectItem>
                  <SelectItem value="desinfection">Désinfection</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Date</Label>
                <Input 
                  type="date"
                  value={newLavage.date_lavage}
                  onChange={(e) => setNewLavage(prev => ({...prev, date_lavage: e.target.value}))}
                />
              </div>
              <div className="space-y-2">
                <Label>Heure</Label>
                <Input 
                  type="time"
                  value={newLavage.heure_lavage}
                  onChange={(e) => setNewLavage(prev => ({...prev, heure_lavage: e.target.value}))}
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Opérateur</Label>
              <Input 
                value={newLavage.operateur_nom}
                onChange={(e) => setNewLavage(prev => ({...prev, operateur_nom: e.target.value}))}
                placeholder="Nom de l'opérateur"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Client (ETA)</Label>
              <Input 
                value={newLavage.client_nom}
                onChange={(e) => setNewLavage(prev => ({...prev, client_nom: e.target.value}))}
                placeholder="Optionnel - Nom du client bio"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLavageDialog(false)}>
              Annuler
            </Button>
            <Button onClick={createLavage} disabled={!newLavage.materiel_nom}>
              <CheckCircle2 className="w-4 h-4 mr-2" />
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
